import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getUserPaymentHistory } from '../api';

const PaymentHistory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchPaymentHistory();
  }, [user]);

  const fetchPaymentHistory = async (page = 1, isLoadMore = false) => {
    if (!user?.userId) {
      setError('User not found');
      setLoading(false);
      return;
    }

    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const response = await getUserPaymentHistory(user.userId, page, ITEMS_PER_PAGE);
      
      console.log('🔍 Backend Response:', response.data); // DEBUG
      
      if (response?.data?.success) {
        const newData = {
          ...response.data,
          history: response.data.data || []  // Map data → history for table
        };

        if (isLoadMore && paymentData?.history) {
          // Append new items to existing history
          setPaymentData({
            ...newData,
            history: [...paymentData.history, ...newData.history]
          });
        } else {
          // First load or page reset
          setPaymentData(newData);
        }
        
        setCurrentPage(page);
      } else {
        setError('Failed to load payment history');
      }
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setError(err.response?.data?.message || 'Failed to load payment history');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (['success', 'completed'].includes(statusLower)) return 'status-success';
    if (statusLower === 'failed') return 'status-failed';
    if (statusLower === 'pending') return 'status-pending';
    return 'status-unknown';
  };

  const getStatusEmoji = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (['success', 'completed'].includes(statusLower)) return '✓';
    if (['failed', 'failure'].includes(statusLower)) return '❌';
    if (statusLower === 'pending') return '⏳';
    return '❓';
  };

  const maskTxnId = (txnid, visible = 4) => {
    if (!txnid) return '';
    const s = String(txnid);
    if (s.length <= visible) return `XXX${s}`;
    const last = s.slice(-visible);
    return `XXXX${last}`;
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    fetchPaymentHistory(nextPage, true);
  };

  if (loading) {
    return (
      <div className="fb-page-wrapper">
        <div className="container">
          <div className="fb-top-section">
            <button className="fb-close-button" onClick={() => navigate('/home')}>×</button>
            <div className="fb-type-emoji">⏳</div>
            <h1 className="heading">Payment History</h1>
          </div>
          <div className="fb-input-form">
            <p className="refer-subtitle">Loading payment history...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fb-page-wrapper">
        <div className="container">
          <div className="fb-top-section">
            <button className="fb-close-button" onClick={() => navigate('/home')}>×</button>
            <div className="error-icon">
              <span className="error-icon-symbol">⚠</span>
            </div>
            <h1 className="heading">Payment History</h1>
          </div>
          <div className="fb-input-form">
            <p className="payment-error-text">{error}</p>
            <button className="retry-payment-btn" onClick={fetchPaymentHistory}>
              <span>🔄</span>
              <span>Retry</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fb-page-wrapper">
      <div className="container">
        {/* Header */}
        <div className="fb-top-section">
          <button className="fb-close-button" onClick={() => navigate('/home')}>×</button>
          <div className="fb-type-emoji">📜</div>
          <h1 className="heading">Payment History</h1>
          <p className="refer-subtitle">
            {paymentData.count || 0} transactions found
          </p>
        </div>

        <div className="fb-input-form">
          {/* Transactions Table */}
          {!paymentData?.history || paymentData.history.length === 0 ? (
            <div className="payment-empty-state">
              <div className="fb-type-emoji">📭</div>
              <p className="payment-empty-text">No payment history found</p>
              <button className="credit-package" onClick={() => navigate('/credits')}>
                <span className="fb-type-emoji">💎</span>
                <span className="fb-type-heading">Buy Credits</span>
              </button>
            </div>
          ) : (
            <>
            <div className="payment-table-container">
              <div className="payment-table-wrapper">
                <table className="payment-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Transaction ID</th>
                      <th>Amount</th>
                      <th>Credits</th>
                      <th>Gateway</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* ✅ FIXED: Direct flat structure from backend */}
                    {paymentData.history.map((item, index) => (
                      <tr key={item.txnid || index}>
                        <td data-label="Date">
                          <div className="table-cell-content">
                            <span>{formatDate(item.created_at)}</span>
                          </div>
                        </td>
                        <td data-label="Transaction ID">
                          <div className="table-cell-content">
                            <span className="txn-id" title={item.txnid}>
                              {maskTxnId(item.txnid, 4)}
                            </span>
                          </div>
                        </td>
                        <td data-label="Amount">
                          <div className="table-cell-content">
                            <span className="amount-value">
                              ₹{parseFloat(item.amount || 0).toFixed(2)}
                            </span>
                          </div>
                        </td>
                        <td data-label="Credits">
                          <div className="table-cell-content">
                            <span className="credits-value">
                              {item.credits_purchased || 0}
                            </span>
                          </div>
                        </td>
                        <td data-label="Gateway">
                          <div className="table-cell-content">
                            <span>{item.gateway || 'PAYU'}</span>
                          </div>
                        </td>
                        <td data-label="Status">
                          <span className={`status-badge ${getStatusBadgeClass(item.status)}`}>
                            <span className="status-emoji">{getStatusEmoji(item.status)}</span>
                            <span className="status-text">{item.status || 'Unknown'}</span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Load More Section */}
            {paymentData?.hasMore && (
              <div className="payment-load-more-section">
                <button 
                  className="payment-load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <span className="load-spinner">⏳</span>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <span>Load More Transactions</span>
                  )}
                </button>
                <p className="load-more-info">
                  Showing {paymentData?.history?.length || 0} of {paymentData?.totalCount || 0} transactions
                </p>
              </div>
            )}
            </>
          )}

          {/* Action Buttons */}
          {/* <div className="fb-button-group">
            <button
              type="button"
              className="credit-package"
              onClick={() => navigate('/home')}
            >
              <span className="fb-type-emoji">🏠</span>
              <span className="fb-type-heading">Home</span>
            </button>

            <button
              type="button"
              className="credit-package"
              onClick={() => navigate('/credits')}
            >
              <span className="fb-type-emoji">💎</span>
              <span className="fb-type-heading">Buy Credits</span>
            </button>
          </div> */}

          {/* Security Footer */}
          <p className="refer-subtitle nomargin">
            🔒 Secure payments powered by PayU • All transactions are encrypted
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;
